<template>
  <div
    class="tw-rounded-sm tw-bg-white tw-px-4 tw-py-3 tw-h-fit"
    :data-test-id="`${tier}-quick_actions-card`"
  >
    <div class="tw-flex">
      <div class="tw-grow tw-pb-2">
        <div class="tw-text-lg tw-font-semibold">
          {{ t("quick-actions") }}
        </div>
      </div>
      <span
        v-tippy="tooltip"
        class="tw-text-primary-grey-200 tw-mt-3"
        :data-test-id="`${tier}-quick_actions_info-icon`"
      >
        <cb-icon-information :width="18" />
      </span>
    </div>
    <div>
      <ul>
        <authorisation-wrapper
          v-if="!embedded"
          :resource="ACCOUNTS"
          :action="WRITE"
          v-slot="{ isAllowed: isWriteAllowed }"
        >
          <li key="addCustomer">
            <a
              class="tw-btn tw-btn-primary tw-flex tw-items-center"
              :data-test-id="`${tier}-add_customer`"
              :href="addCustomerHref"
              @click.prevent="
                () => {
                  if (isWriteAllowed) {
                    goToAddCustomer(true);
                  }
                }
              "
            >
              <template v-if="isWriteAllowed">
                <span class="tw-p-3 tw-text-primary-grey-950">
                  <component :is="'cb-icon-customers'" />
                </span>
                <span
                  :class="'tw-font-semibold tw-text-primary-grey-950 hover:tw-underline hover:tw-cursor-pointer'"
                >
                  {{ t("add-new-customer") }}
                </span>
              </template>
              <template v-else>
                <span class="tw-p-3 tw-text-primary-grey-200">
                  <component :is="'cb-icon-customers'" />
                </span>
                <span
                  :class="'tw-font-semibold tw-text-primary-grey-200 hover:tw-cursor-not-allowed'"
                >
                  {{ t("add-new-customer") }}
                </span>
              </template>
            </a>
          </li>
        </authorisation-wrapper>

        <authorisation-wrapper
          :resource="EMAIL_SECURITY"
          :action="WRITE"
          v-slot="{ isAllowed: isWriteAllowed }"
        >
          <li key="manageRemediationOptions">
            <a
              class="tw-btn tw-btn-primary tw-flex tw-items-center"
              :data-test-id="`${tier}-go_to_redmediation`"
              :href="remediationHref"
              @click.prevent="
                () => {
                  if (isWriteAllowed) {
                    goToManageRemediationOptions();
                  }
                }
              "
            >
              <template v-if="isWriteAllowed">
                <span class="tw-p-3 tw-text-primary-grey-950">
                  <component
                    :is="SERVICE_ICON.PHISH_SERVICE"
                    :data-test-id="`${tier}-email_security-icon`"
                  />
                </span>
                <span
                  :class="
                    isSolutionActive(PHISH_SERVICE)
                      ? 'tw-font-semibold tw-text-primary-grey-950 hover:tw-underline hover:tw-cursor-pointer'
                      : 'tw-text-primary-grey-200 tw-font-normal'
                  "
                  v-tippy="{
                    content: $t('no-email-security-yet'),
                    placement: 'top',
                    onShow: () => !isSolutionActive(PHISH_SERVICE),
                  }"
                >
                  {{ t("manage-remediation-options") }}
                </span>
              </template>
              <template v-else>
                <span class="tw-p-3 tw-text-primary-grey-200">
                  <component
                    :is="SERVICE_ICON.PHISH_SERVICE"
                    :data-test-id="`${tier}-email_security-icon`"
                  />
                </span>
                <span
                  :class="
                    isSolutionActive(PHISH_SERVICE)
                      ? 'tw-font-semibold tw-text-primary-grey-200 hover:tw-cursor-not-allowed'
                      : 'tw-font-normal tw-text-primary-grey-200 hover:tw-cursor-default'
                  "
                  v-tippy="{
                    content: $t('no-email-security-yet'),
                    placement: 'top',
                    onShow: () => !isSolutionActive(PHISH_SERVICE),
                  }"
                >
                  {{ t("manage-remediation-options") }}
                </span>
              </template>
            </a>
          </li>
        </authorisation-wrapper>

        <authorisation-wrapper
          :resource="PHISHING_CAMPAIGNS"
          :action="WRITE"
          v-slot="{ isAllowed: isWriteAllowed }"
        >
          <li key="newPhishingSim">
            <a
              class="tw-btn tw-btn-primary tw-flex tw-items-center"
              :data-test-id="`${tier}-go_to_phish_simulation`"
              :href="phishHref"
              @click.prevent="
                () => {
                  if (isWriteAllowed) {
                    goToNewPhishingSim();
                  }
                }
              "
            >
              <template v-if="isWriteAllowed">
                <span class="tw-p-3 tw-text-primary-grey-950">
                  <component
                    :is="SERVICE_ICON.PHISHING_SIMULATION"
                    :data-test-id="`${tier}-phishing_simulation-icon`"
                  />
                </span>
                <span
                  :class="[
                    isSolutionActive(PHISHING_SIMULATION)
                      ? 'tw-font-semibold tw-text-primary-grey-950 hover:tw-underline hover:tw-cursor-pointer'
                      : 'tw-text-primary-grey-200 tw-font-normal',
                  ]"
                  v-tippy="{
                    content: $t('no-phs-yet'),
                    placement: 'top',
                    onShow: () => !isSolutionActive(PHISHING_SIMULATION),
                  }"
                >
                  {{ t("new-phishing-campaign") }}
                </span>
              </template>
              <template v-else>
                <span class="tw-p-3 tw-text-primary-grey-200">
                  <component
                    :is="SERVICE_ICON.PHISHING_SIMULATION"
                    :data-test-id="`${tier}-phishing_simulation-icon`"
                  />
                </span>
                <span
                  :class="
                    isSolutionActive(PHISHING_SIMULATION)
                      ? 'tw-font-semibold tw-text-primary-grey-200 hover:tw-cursor-not-allowed'
                      : 'tw-font-normal tw-text-primary-grey-200 hover:tw-cursor-default'
                  "
                  v-tippy="{
                    content: $t('no-email-security-yet'),
                    placement: 'top',
                    onShow: () => !isSolutionActive(PHISHING_SIMULATION),
                  }"
                >
                  {{ t("new-phishing-campaign") }}
                </span>
              </template>
            </a>
          </li>
        </authorisation-wrapper>

        <authorisation-wrapper
          :resource="SAT_CAMPAIGNS"
          :action="WRITE"
          v-slot="{ isAllowed: isWriteAllowed }"
        >
          <li key="newSAT">
            <a
              class="tw-btn tw-btn-primary tw-flex tw-items-center"
              :data-test-id="`${tier}-go_to_sat`"
              :href="satHref"
              @click.prevent="
                () => {
                  if (isWriteAllowed) {
                    goToNewSAT();
                  }
                }
              "
            >
              <template v-if="isWriteAllowed">
                <span class="tw-p-3 tw-text-primary-grey-950">
                  <component
                    :is="SERVICE_ICON.SAT_SERVICE"
                    :data-test-id="`${tier}-sat-icon`"
                  />
                </span>
                <span
                  :class="
                    isSolutionActive(SAT_SERVICE)
                      ? 'tw-font-semibold tw-text-primary-grey-950 hover:tw-underline hover:tw-cursor-pointer'
                      : 'tw-text-primary-grey-200 tw-font-normal'
                  "
                  v-tippy="{
                    content: $t('no-sat-yet'),
                    placement: 'top',
                    onShow: () => !isSolutionActive(SAT_SERVICE),
                  }"
                >
                  {{ t("schedule-sat") }}
                </span>
              </template>
              <template v-else>
                <span class="tw-p-3 tw-text-primary-grey-200">
                  <component
                    :is="SERVICE_ICON.SAT_SERVICE"
                    :data-test-id="`${tier}-sat-icon`"
                  />
                </span>
                <span
                  :class="
                    isSolutionActive(SAT_SERVICE)
                      ? 'tw-font-semibold tw-text-primary-grey-200 hover:tw-cursor-not-allowed'
                      : 'tw-font-normal tw-text-primary-grey-200 hover:tw-cursor-default'
                  "
                  v-tippy="{
                    content: $t('no-email-security-yet'),
                    placement: 'top',
                    onShow: () => !isSolutionActive(SAT_SERVICE),
                  }"
                >
                  {{ t("schedule-sat") }}
                </span>
              </template>
            </a>
          </li>
        </authorisation-wrapper>
      </ul>
    </div>
  </div>
</template>
<script setup lang="ts">
import { ref } from "vue";
import { useI18n } from "vue-i18n";
import {
  PHISHING_SIMULATION,
  PHISH_SERVICE,
  SAT_SERVICE,
} from "@/utils/services";
import { SERVICE_ICON } from "@/utils/services";
import { useActiveSolutions } from "@/composables/useActiveSolutions";
import { useRouter } from "vue-router";
import { useIsMspRoute } from "@/composables/useIsMspRoute";
import { buildOrgUrl } from "@/utils/navigation/orgUrl";
import { computed } from "vue";
import { isEmbedded } from "@/remote/contract";

import {
  AuthorisationWrapper,
  PHISHING_CAMPAIGNS,
  SAT_CAMPAIGNS,
  EMAIL_SECURITY,
  ACCOUNTS,
  WRITE,
} from "@/authorisation";

const router = useRouter();
const { isSolutionActive } = useActiveSolutions();
const { t } = useI18n();

const { isMspRoute } = useIsMspRoute();

// Hosts that embed the remote provide their own customer management; suppress the
// remote's Add Customer affordance so it can't create orgs behind the host's back.
const embedded = isEmbedded();

const tier = isMspRoute.value ? "msp" : "customer";
const addCustomerHref = buildOrgUrl("msp", null, "customers?openModal=true");
const phishHref = computed(() =>
  buildOrgUrl(tier, null, "phishing_simulation/overview/create_campaign"),
);
const satHref = computed(() =>
  buildOrgUrl(tier, null, "security_awareness_training/overview/schedule_sat"),
);
const goToAddCustomer = (openModal: boolean = true): void => {
  router.push(
    openModal ? addCustomerHref : buildOrgUrl("msp", null, "customers"),
  );
};
const tooltip = ref({
  content: t("quick-actions-dashboard"),
  placement: "top",
});
const remediationHref = buildOrgUrl(
  "msp",
  null,
  "email_security/configuration",
);
const goToManageRemediationOptions = (): void => {
  if (isSolutionActive(PHISH_SERVICE)) {
    router.push(remediationHref);
  }
};

const goToNewPhishingSim = (): void => {
  if (isSolutionActive(PHISHING_SIMULATION)) {
    router.push(phishHref.value);
  }
};

const goToNewSAT = (): void => {
  if (isSolutionActive(SAT_SERVICE)) {
    router.push(satHref.value);
  }
};
</script>
